import { Fragment, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, IconButton, validationUtils, WithTooltip } from 'basic-components';
import { Clear, Info } from 'basic-components/icons';

import { PickersUtils } from 'components/primary/pickers/Utils';
import Modal from 'components/primary/pickers/PipelinePicker/Modal';

import { Else, If, Then, When } from 'react-if';

import classNames from 'classnames';
import '../Picker.scss';

const EMPTY_ARRAY = [];
const EMPTY_OBJ = {};
export const PipelinePicker = ({ disabled = false, size = 'normal', tooltipProps = EMPTY_OBJ, optionalText = undefined, onChange, name, defaultValue = undefined, validators = EMPTY_ARRAY, required = false, withRootId = false, triggerType = undefined, ref }) => { 
  const [isOpen, open] = useState(false),
        [value, setValue] = useState(),
        [isValid, setValid] = useState(true),
        [errorMessage, setErrorMessage] = useState(undefined),
        [isOverflowed, setOverflowed] = useState(false),

        refErrorMessage = useRef(null),

        openModal = useCallback(() => open(true), [open]),
        closeModal = useCallback(() => open(false), [open]),
        clearValue = useCallback(() => !disabled && setValue(""), [disabled]),
        onSubmit = useCallback((localValue, newest) => {
          const isNewest = typeof newest === "boolean" ? newest : (newest === "true")
          const newVal = {
            name: localValue.name,
            id: isNewest ? undefined : localValue.id,
            groupName: localValue.groupName
          }
          setValue(withRootId ? Object.assign(newVal, { rootId: localValue.root }) : newVal)
        }, [withRootId]),
        validate = useCallback(v => validationUtils.validateNoState(v, validators, name, required, setValid, setErrorMessage), [name, required, validators])
    
  useEffect(() => {
    setValue(defaultValue)
  }, [defaultValue])

  useEffect(() => {
    value !== undefined && onChange(value, name)
  }, [value, name, onChange])

  useEffect(() => {
    validate(typeof value === 'object' ? value.name : value)
  }, [value, validate])

  useImperativeHandle(ref, () => ({ 
    validateForForm: () => validate(typeof value === 'object' ? value.name : value),
    isValid: () => isValid
  }))
  useEffect(() => {
    if(refErrorMessage.current && refErrorMessage.current.clientWidth < refErrorMessage.current.scrollWidth) {
      setOverflowed(true)
    } else {
      setOverflowed(false)
    }
  }, [isValid, errorMessage])

  return (
    <div
        className="ng-common-picker"
        onSubmit={event => event.stopPropagation()}
    >
      <div className={classNames("ng-common-picker__row",  { "ng-common-picker__row--choosen": value !== "" && value !== undefined && value !== " " }, { "ng-common-picker__row--not-choosen": value === "" || value === undefined || value === " " })}>
        <If condition={value !== "" && value !== undefined  && value !== " "}>
          <Then>
            {() => (
              <Fragment>
                <div className="ng-common-picker__row--choosen__top">
                  <When condition={value && !value?.id}>
                    {() => (
                      <WithTooltip
                          data-place="top"
                          text="To run pipeline we take the newest version of this pipeline."
                      >
                        <p className="ng-common-picker__row--choosen__top__latest">Take newest</p>
                      </WithTooltip>
                    )}
                  </When>
                  <p
                      className="ng-common-picker__row--choosen__top__name"
                      data-testid="pipeline-name"
                  >{value?.name}</p>
                  <IconButton
                      className={classNames(
                        "ng-common-picker__row--choosen__top__clear",
                        { "ng-common-picker__row--choosen__top__clear--is-disabled": disabled }
                      )}
                      onClick={clearValue}
                  >
                    <Clear/>
                  </IconButton>
                </div>
                <p 
                    className="ng-common-picker__row--choosen__optional"
                    data-testid="pipeline-groupname"
                >{`Group: ${value?.groupName || "Unknown"}`}</p>
              </Fragment>
            )}
          </Then>
          <Else>
            {() => (
              <Button
                  disabled={disabled}
                  onClick={openModal}
                  size={size}
                  type="button"
                  variant="text"
              >
                Select Pipeline
              </Button>
            )}
          </Else>
        </If>
        <div
            className={classNames("ng-common-picker__error-container", { "ng-common-picker__error-container--is-none": isValid })}
            data-testid="text-field-error-container"
        >
          <p ref={refErrorMessage}>
            {errorMessage}
          </p>
          <When condition={isOverflowed}>
            {() => PickersUtils.getErrorTooltip(errorMessage, "ng-common-picker__error-container__more")}
          </When>
        </div>
      </div>
      <When condition={Object.keys(tooltipProps || {})?.length > 0}>
        {() => (
          <div 
              className="ng-common-picker__tooltip-container"
              data-place="left"
              {...tooltipProps}
          >
            <Info/>
          </div>
        )}
      </When>
      <When condition={isOpen}>
        {() => (
          <Modal
              closeModal={closeModal}
              defaultValue={defaultValue}
              onSubmit={onSubmit}
              optionalText={optionalText}
              triggerType={triggerType}
              withRootId={withRootId}
          />
        )}
      </When>
    </div>
  )
}


PipelinePicker.propTypes = {
  defaultValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  disabled: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  optionalText: PropTypes.string,
  required: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'normal', 'large']),
  tooltipProps: PropTypes.object,
  validators: PropTypes.arrayOf(PropTypes.shape({
    message: PropTypes.string.isRequired,
    validator: PropTypes.func.isRequired
  })),
  withRootId: PropTypes.bool
}

PipelinePicker.displayName = 'PipelinePicker'
